import api from './http'
import type {
    OrganizerRegistration,
    ParticipantRegistration,
    CompanyRegistration,
    RequestEmailValidationWithPinResponse,
    RequestEmailChangeResponse,
    ConfirmEmailChangeResponse,
    OrganizerRegisterResponse,
} from '@/types/registration'
import type { Organizer, OrganizerStrapiResponse, Participant } from '@/types/users'
import type { StrapiResponse } from '@/types/strapi.ts'
import { type AxiosRequestConfig, type AxiosResponse } from 'axios'
export type AxiosRequestConfigWithSuppressGlobalErrorToast = AxiosRequestConfig & {
    suppressGlobalErrorToast?: boolean
    withCredentials?: boolean
}

export function login(identifier: string, password: string, rememberMe: boolean) {
    const config: AxiosRequestConfigWithSuppressGlobalErrorToast = {
        withCredentials: true,
        suppressGlobalErrorToast: true,
    }
    return api.post('/auth/local', { identifier, password, rememberMe }, config)
}

export function logout() {
    return api.post('/auth/logout', {}, { withCredentials: true })
}

export function requestPasswordReset(email: string) {
    return api.post('/auth/forgot-password', { email }, { withCredentials: true })
}

export function resetPassword(data: {
    code: string
    password: string
    passwordConfirmation: string
}) {
    return api.post('/auth/reset-password', data)
}

export async function changePassword(data: {
    currentPassword: string
    password: string
    passwordConfirmation: string
}): Promise<AxiosResponse<{ success: true; jwt: string }>> {
    const config: AxiosRequestConfigWithSuppressGlobalErrorToast = {
        suppressGlobalErrorToast: true,
    }
    return api.post('/users/me/password-change', data, config)
}

export async function registerOrganizer(
    data: OrganizerRegistration
): Promise<OrganizerRegisterResponse> {
    const response = await api.post('/organizer/register', data)
    return response.data
}

export async function registerOrganization(data: CompanyRegistration) {
    return api.post('/company/register', data)
}

export async function registerParticipant(data: ParticipantRegistration) {
    return (await api.post('/participant/register', data)).data
}

export async function getOrganizerByCurrentUser() {
    const response = await api.get<OrganizerStrapiResponse>('/organizer/me', {
        headers: { 'Cache-Control': 'no-cache' },
    })
    return response.data
}

export async function destroyOrganizerAccount(): Promise<{ success: true }> {
    await api.delete('/organizer/destroy-account', {
        suppressGlobalErrorToast: true,
    } as AxiosRequestConfigWithSuppressGlobalErrorToast)

    return { success: true }
}

export async function getParticipantByUserId(userId: number) {
    // Why: Strapi requires nested filter/populate params for relation queries; keep object form for correct serialization.
    const params: Record<string, unknown> = {
        filters: {
            user: {
                id: {
                    $eq: userId,
                },
            },
        },
        populate: '*',
    }

    const response = await api.get<StrapiResponse<Participant[]>>('/participants', { params })
    return response.data.data[0]
}

export async function destroyParticipantAccount(): Promise<{ success: true }> {
    await api.delete('/participant/destroy-account', {
        suppressGlobalErrorToast: true,
    } as AxiosRequestConfigWithSuppressGlobalErrorToast)

    return { success: true }
}

export async function updateOrganizerByDocumentId(
    id: string,
    data: { data: Partial<Organizer> }
): Promise<Organizer> {
    return (await api.put(`/organizers/${id}`, data)).data
}

export async function checkOrganizerExists(email: string): Promise<{ exists: boolean }> {
    const response = await api.post('/organizer/login-check', { email })
    return response.data
}

export async function checkParticipantExists(email: string): Promise<{ exists: boolean }> {
    const response = await api.post('/participant/login-check', { email })
    return response.data
}

export interface RefreshTokenResponse {
    jwt: string
    refreshToken?: string
}

export async function refreshToken(): Promise<RefreshTokenResponse | null> {
    const { data } = await api.post<RefreshTokenResponse>(
        '/auth/token/refresh',
        {},
        { withCredentials: true }
    )
    if (!data) {
        return null
    }
    if (!data.jwt) {
        throw new Error('Missing jwt in refresh response')
    }
    return data
}

export async function verifyEmailWithPin(email: string, pin: string) {
    return api.post('/verification-pin/validate-email', { email, pin }, {
        suppressGlobalErrorToast: true,
    } as AxiosRequestConfigWithSuppressGlobalErrorToast)
}

export async function requestEmailValidationPin(
    email: string
): Promise<AxiosResponse<RequestEmailValidationWithPinResponse>> {
    return api.post<RequestEmailValidationWithPinResponse>(
        '/verification-pin/request-email-validation-pin',
        { email }
    )
}

export async function requestPasswordResetPin(email: string) {
    return api.post('/verification-pin/request-password-reset-pin', { email })
}

export async function setNewPassword(email: string, pin: string, password: string) {
    return api.post('/verification-pin/confirm-password-reset', { email, pin, password }, {
        suppressGlobalErrorToast: true,
    } as AxiosRequestConfigWithSuppressGlobalErrorToast)
}

export async function requestEmailChange(
    currentPassword: string,
    newEmail: string
): Promise<AxiosResponse<RequestEmailChangeResponse>> {
    return api.post<RequestEmailChangeResponse>(
        '/users/me/email-change',
        { currentPassword, newEmail },
        { suppressGlobalErrorToast: true } as AxiosRequestConfigWithSuppressGlobalErrorToast
    )
}

export async function confirmEmailChange(
    pin: string
): Promise<AxiosResponse<ConfirmEmailChangeResponse>> {
    return api.post<ConfirmEmailChangeResponse>('/users/me/email-change/confirm', { pin }, {
        suppressGlobalErrorToast: true,
    } as AxiosRequestConfigWithSuppressGlobalErrorToast)
}
